import React, { useCallback } from 'react';
import { isNull } from 'lodash';
import { isUndefined } from 'lodash/fp';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { Scrollable } from '../../../shared/scrollable/Scrollable';
import { OverflowTooltip } from '../../../shared/tooltip';
import { getCurrentSelectedQueries, updateSelectedDocumentQuery, deletePlaygroundDocumentQuery, getAIResults, setSchedulePage, getCurrentCorrectQueries, updateCorrectPlaygroundQueries } from './store';
import styles from './AIPlayground.module.scss';
import { LongText } from '../../../shared/longtext/LongText';
import { FieldValue } from '../../../datasets/instances/store';
import { Text } from '../../../shared/text/Text';
import { IconButton } from '../../../shared/button/IconButton';
import { Delete, DocumentHighlight, DocumentHighlightWithQuestionMark } from '../../../shared/icons';
import { MLAnswer } from '../../../datasets/instances/ml-modal/MLAnswer';
import { Toggle } from '../../../shared/toggle';
import { MLDataValue } from '../../../datasets/store/mlTypes';
import { getIsCheckingForHighlights, PDFPageHighlights, setIndexesToHighlight } from '../../../documents/document-viewer/store';
import { getCurrentDocumentQueries } from '../queries/store/selectors';
import { DocumentQuery } from '../queries/store/types';

const { red, yellowGreen } = styles;

export const AIPlaygroundQuery: React.FC = () => {
    const dispatch = useAppDispatch();

    const selectedQueries = useAppSelector(getCurrentSelectedQueries);
    const aiResults = useAppSelector(getAIResults);
    const currentDocumentQueries = useAppSelector(getCurrentDocumentQueries);
    const currentCorrectQueries = useAppSelector(getCurrentCorrectQueries);
    const checkingForHighlights = useAppSelector(getIsCheckingForHighlights);

    const updateQuery = useCallback((index: number, key: keyof DocumentQuery, value: string | number | number[] | FieldValue) => dispatch(updateSelectedDocumentQuery(index, key, value)), [dispatch]);
    const deleteNewQuery = useCallback((index: number) => dispatch(deletePlaygroundDocumentQuery(index)), [dispatch]);

    const toggleQuery = useCallback((index: number) => {
        let updatedCorrectQueries = [...currentCorrectQueries, index];
        if (currentCorrectQueries.includes(index)) {
            updatedCorrectQueries = currentCorrectQueries.filter(value => value !== index);
        }
        dispatch(updateCorrectPlaygroundQueries(updatedCorrectQueries));
    }, [currentCorrectQueries, dispatch]);

    const scrollToHighlightedAnswer = useCallback((page: number, highlightIndex: number) => {
        const answer = document.getElementById(`highlight-ref-${page}-${highlightIndex}`);
        if (!isNull(answer)) {
            answer.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
            dispatch(setSchedulePage(page));
        }
    }, [dispatch]);

    const highlightPdfAnswer = useCallback((value: PDFPageHighlights) => dispatch(setIndexesToHighlight(value)), [dispatch]);

    const highlightAnswer = useCallback((page: number | undefined, highlightIndexes: number[] | undefined) => {
        if (page && highlightIndexes) {
            highlightPdfAnswer({ page, highlightIndexes });
            scrollToHighlightedAnswer(page, highlightIndexes[0]);
        } else if (page) {
            const pageToScrollTo = document.getElementById(`pdf-page-${page}`);
            if (!isNull(pageToScrollTo)) {
                pageToScrollTo.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }, [highlightPdfAnswer, scrollToHighlightedAnswer]);

    if (selectedQueries.length === 0) {
        return (
            <div className={styles.aiPlaygroundQueryEditScrollableWrapper}>
                <div className={styles.aiPlaygroundEmptyQueries}>Please click the New Question button to add questions</div>
            </div>
        );
    }

    if (!isNull(aiResults)) {
        return (
            <div className={styles.aiPlaygroundQueryEditScrollableWrapper}>
                <div className={styles.mlDatasetTableHeader}>
                    <div className={styles.questionHeaderCell}>Question</div>
                    <div className={styles.answerHeaderCell}>AI Answer</div>
                    <div className={styles.selectHeaderCell}>Correct Answer?</div>
                    <div className={styles.iconHeaderCell}>Highlight</div>
                </div>
                <div className={styles.mlDatasetFindings}>
                    <Scrollable>
                        {selectedQueries.map(({ question, queryKey }, index) => {
                            const entry = aiResults[queryKey];
                            let aiAnswer: MLDataValue = 'Not found';
                            let indexesToHighlight: number[] | undefined = undefined;
                            let pageNumber: number | undefined = undefined;
                            let isAFuzzyMatchHighlight = false;
                            if (!isUndefined(entry)) {
                                const { value, highlightIndexes, page, isFuzzyMatchHighlight } = entry;
                                if (value) {
                                    aiAnswer = value;
                                    indexesToHighlight = highlightIndexes;
                                    pageNumber = page;
                                    isAFuzzyMatchHighlight = !!isFuzzyMatchHighlight;
                                }
                            }
                            const showPdfHighlight = aiAnswer !== 'Not found' && (!!pageNumber || !isUndefined(indexesToHighlight) && indexesToHighlight.length > 0);
                            const highlightIcon = !checkingForHighlights && (isUndefined(indexesToHighlight) || isAFuzzyMatchHighlight) ? DocumentHighlightWithQuestionMark : DocumentHighlight;
                            return (
                                <div key={index} className={styles.mlDatasetQueryWrapper}>
                                    <div className={styles.mlDatasetQuestion}>{question}</div>
                                    <MLAnswer answer={aiAnswer} includeDatasetField depth={1} question={question} queryKey={queryKey} />
                                    <div className={styles.selectWrapper} >
                                        <Toggle checked={currentCorrectQueries.includes(index)} onChange={() => toggleQuery(index)} />
                                    </div>
                                    <div className={styles.highlightIcon}>{showPdfHighlight && <IconButton icon={highlightIcon} showSpinner={checkingForHighlights} iconFill={yellowGreen} onClick={() => highlightAnswer(pageNumber, indexesToHighlight)} />}</div>
                                </div>
                            );
                        })}
                    </Scrollable>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.aiPlaygroundQueryEditScrollableWrapper}>
            <Scrollable>
                {selectedQueries.map(({ documentQueryId, queryKey, question, guidanceNotes }, index) => {
                    let historicalResults: { percentage: number, correctQueries: number, totalQueries: number } | null = null;
                    if (documentQueryId) {
                        const { percentage, correctQueries, totalQueries } = currentDocumentQueries.find(({ queryKey }) => queryKey === queryKey)!;
                        historicalResults = { percentage, correctQueries, totalQueries };
                    }
                    return (
                        <div key={index} className={styles.aiPlaygroundQueryEditWrapper} id={`ai-query-${index}`}>
                            <div className={styles.aiPlaygroundEditDataPointWrapper} key={documentQueryId}>
                                <div className={styles.aiPlaygroundEditDataPointLabel}>Question</div>
                                <LongText testId='ai-playground-query-question' value={question} marginBottom='0px' onChange={value => updateQuery(index, 'question', value)} width='80%' maxLength={1000} />
                            </div>
                            <div className={styles.aiPlaygroundEditDataPointWrapper}>
                                <div className={styles.aiPlaygroundEditDataPointLabel}>Guidance Notes</div>
                                <LongText testId='ai-query-guidance-notes' value={guidanceNotes} marginBottom='0px' onChange={value => updateQuery(index, 'guidanceNotes', value)} width='80%' maxLength={1000} />
                            </div>
                            <div className={styles.aiPlaygroundEditDataPointWrapper}>
                                <div className={styles.aiPlaygroundEditDataPointLabel}>Key</div>
                                <Text testId='ai-playground-query-key' value={queryKey} disabled={!isUndefined(documentQueryId)} onChange={e => updateQuery(index, 'queryKey', e.target.value)} marginBottom='0px' width='80%' />
                            </div>
                            {historicalResults &&
                                <div className={styles.percentageWrapper}>
                                    <div className={styles.percentageBar}>
                                        <div className={styles.successPercentage} style={{ width: `${historicalResults.percentage}%` }} />
                                    </div>
                                    <div className={styles.successInfo}>
                                        <div className={styles.successString}>
                                            <OverflowTooltip overlayText={`Historical success rate: ${historicalResults.correctQueries} / ${historicalResults.totalQueries}`} />
                                        </div>
                                        <div className={styles.displayPercentage}>{historicalResults.percentage}%</div>
                                    </div>
                                </div>
                            }
                            {isUndefined(documentQueryId) &&
                                <div className={styles.aiPlaygroundEditDeleteQuery}>
                                    <IconButton icon={Delete} onClick={() => deleteNewQuery(index)} color={red} />
                                </div>
                            }
                        </div>
                    );
                })}
            </Scrollable>
        </div>
    );
};
